/** Small hand-rolled SVG charts: no chart library, consistent styling, hover readouts. */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from './ui';

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e!.contentRect.width)));
    ro.observe(ref.current);
    setW(Math.floor(ref.current.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** Round axis ticks (1, 2, 2.5, 5 × 10ⁿ). */
function ticks(min: number, max: number, count = 4) {
  // A flat series (a new company's all-zero months) gets one baseline instead of made-up fractions of a cent.
  if (min === max) return [min];
  const span = max - min;
  const step0 = span / count;
  const mag = 10 ** Math.floor(Math.log10(step0));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= step0) ?? step0;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

export interface Series {
  name: string;
  values: number[];
  color?: string;
}

/** What a chart shows, as text for circle to ask (lib/region.ts reads it instead of the axis labels). */
function explainText(labels: string[], series: Series[], format: (v: number) => string) {
  const one = (s: Series) => `${s.name}: ${labels.map((l, i) => `${l} ${s.values[i] === undefined ? 'n/a' : format(s.values[i]!)}`).join(', ')}`;
  return `Chart. ${series.map(one).join('. ')}`.slice(0, 2000);
}

/** A sparkline has no units, so circle to ask gets its shape: direction, size of the change, where it peaked. */
function sparkText(values: number[]) {
  const first = values[0]!;
  const last = values[values.length - 1]!;
  const change = first ? ((last - first) / Math.abs(first)) * 100 : 0;
  const high = values.indexOf(Math.max(...values)) + 1;
  const low = values.indexOf(Math.min(...values)) + 1;
  return `Small trend line, ${values.length} points oldest first: ${change >= 0 ? 'up' : 'down'} ${Math.abs(change).toFixed(1)}% from first to last, highest at point ${high}, lowest at point ${low}.`;
}

const PALETTE = ['var(--color-series-1)', 'var(--color-series-2)', 'var(--color-series-3)', 'var(--color-series-4)', 'var(--color-series-5)'];

function Readout({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  const left = Math.min(Math.max(x - 80, 0), Math.max(0, width - 170));
  return (
    <div className="glass-float pointer-events-none absolute top-0 z-10 w-[170px] rounded-lg px-2.5 py-1.5 text-[11.5px]" style={{ left }}>
      {children}
    </div>
  );
}

export function Legend({ series }: { series: { name: string; color?: string; dashed?: boolean }[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px] text-muted">
      {series.map((s, i) => (
        <span key={s.name} className="inline-flex items-center gap-1.5">
          <span className={cx('inline-block h-0.5 w-3 rounded', s.dashed && 'border-t border-dashed bg-transparent')} style={{ background: s.dashed ? undefined : (s.color ?? PALETTE[i]), borderColor: s.color ?? PALETTE[i] }} />
          {s.name}
        </span>
      ))}
    </div>
  );
}

/** Show every k-th axis label so labels (about 46px wide) never collide. */
const labelEvery = (n: number, plotW: number) => Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 46))));

/** Grouped or stacked vertical bars. Negative values hang below the zero line. */
export function BarChart({
  labels,
  series,
  height = 200,
  format,
  stacked,
  axisFormat,
  highlightLast,
}: {
  labels: string[];
  series: Series[];
  height?: number;
  format: (v: number) => string;
  axisFormat?: (v: number) => string;
  stacked?: boolean;
  highlightLast?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const padL = 52;
  const padB = 22;
  const padT = 8;
  const n = labels.length;
  let min = 0;
  let max = 0;
  for (let i = 0; i < n; i++) {
    if (stacked) {
      const pos = series.reduce((s, x) => s + Math.max(0, x.values[i] ?? 0), 0);
      const neg = series.reduce((s, x) => s + Math.min(0, x.values[i] ?? 0), 0);
      max = Math.max(max, pos);
      min = Math.min(min, neg);
    } else for (const s of series) (max = Math.max(max, s.values[i] ?? 0)), (min = Math.min(min, s.values[i] ?? 0));
  }
  const t = ticks(min, max);
  const lo = t[0]!;
  const hi = t[t.length - 1]!;
  const plotW = Math.max(0, width - padL);
  const plotH = height - padB - padT;
  const y = (v: number) => padT + plotH - ((v - lo) / (hi - lo || 1)) * plotH;
  const band = n ? plotW / n : 0;
  const groupW = Math.min(band * 0.68, stacked ? 34 : 18 * series.length + 6);
  const barW = stacked ? groupW : groupW / series.length;

  return (
    <div ref={ref} className="relative w-full" data-explain={explainText(labels, series, format)} onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={height} className="block overflow-visible">
          {t.map((v) => (
            <g key={v}>
              <line x1={padL} x2={width} y1={y(v)} y2={y(v)} stroke={v === 0 ? 'var(--color-line-strong)' : 'var(--color-line)'} strokeDasharray={v === 0 ? undefined : '2 3'} />
              <text x={padL - 8} y={y(v)} dy="0.32em" textAnchor="end" className="num fill-muted text-[10.5px]">
                {(axisFormat ?? format)(v)}
              </text>
            </g>
          ))}
          {labels.map((l, i) => {
            const cx0 = padL + band * i + band / 2;
            let posAcc = 0;
            let negAcc = 0;
            return (
              <g key={l + i} onMouseEnter={() => setHover(i)}>
                <rect x={padL + band * i} y={padT} width={band} height={plotH} fill={hover === i ? 'var(--color-paper)' : 'transparent'} />
                {series.map((s, si) => {
                  const v = s.values[i] ?? 0;
                  let y0: number;
                  let y1: number;
                  let x: number;
                  if (stacked) {
                    x = cx0 - barW / 2;
                    if (v >= 0) (y1 = y(posAcc)), (y0 = y(posAcc + v)), (posAcc += v);
                    else (y0 = y(negAcc)), (y1 = y(negAcc + v)), (negAcc += v);
                  } else {
                    x = cx0 - groupW / 2 + barW * si;
                    y0 = y(Math.max(0, v));
                    y1 = y(Math.min(0, v));
                  }
                  const color = s.color ?? PALETTE[si];
                  return <rect key={s.name} x={x + (stacked ? 0 : 1)} y={y0} width={Math.max(1, barW - (stacked ? 0 : 2))} height={Math.max(v === 0 ? 0 : 1, y1 - y0)} rx={2} fill={color} opacity={highlightLast && i !== n - 1 && hover !== i ? 0.55 : 1} />;
                })}
                {i % labelEvery(n, plotW) === 0 && (
                  <text x={cx0} y={height - 6} textAnchor="middle" className="fill-muted text-[10.5px]">
                    {l}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}
      {hover !== null && (
        <Readout x={padL + band * hover + band / 2} width={width}>
          <div className="mb-1 font-medium text-ink">{labels[hover]}</div>
          {series.map((s, si) => (
            <div key={s.name} className="flex justify-between gap-2">
              <span className="flex items-center gap-1.5 text-muted">
                <span className="size-2 rounded-sm" style={{ background: s.color ?? PALETTE[si] }} />
                {s.name}
              </span>
              <span className="num text-ink">{format(s.values[hover] ?? 0)}</span>
            </div>
          ))}
        </Readout>
      )}
    </div>
  );
}

/** Lines (optionally with an area under the first series). */
export function LineChart({
  labels,
  series,
  height = 200,
  format,
  axisFormat,
  area,
  zeroBased,
  dashedFrom,
}: {
  labels: string[];
  series: Series[];
  height?: number;
  format: (v: number) => string;
  axisFormat?: (v: number) => string;
  area?: boolean;
  zeroBased?: boolean;
  /** Index from which lines are drawn dashed (forecast). */
  dashedFrom?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const padL = 52;
  const padB = 22;
  const padT = 10;
  const n = labels.length;
  const all = series.flatMap((s) => s.values);
  let min = Math.min(...all, zeroBased ? 0 : Infinity);
  let max = Math.max(...all, zeroBased ? 0 : -Infinity);
  if (!Number.isFinite(min)) (min = 0), (max = 1);
  if (!zeroBased) {
    const pad = (max - min) * 0.12 || Math.abs(max) * 0.1;
    min -= pad;
    max += pad;
  }
  const t = ticks(min, max);
  const lo = t[0]!;
  const hi = t[t.length - 1]!;
  const plotW = Math.max(0, width - padL - 8);
  const plotH = height - padB - padT;
  const x = (i: number) => padL + (n <= 1 ? plotW / 2 : (plotW * i) / (n - 1));
  const y = (v: number) => padT + plotH - ((v - lo) / (hi - lo || 1)) * plotH;

  const path = (vals: number[], from = 0, to = vals.length - 1) =>
    vals
      .slice(from, to + 1)
      .map((v, k) => `${k ? 'L' : 'M'}${x(from + k).toFixed(1)},${y(v).toFixed(1)}`)
      .join('');

  return (
    <div
      ref={ref}
      className="relative w-full"
      data-explain={explainText(labels, series, format)}
      onMouseLeave={() => setHover(null)}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const px = e.clientX - r.left - padL;
        if (n) setHover(Math.max(0, Math.min(n - 1, Math.round((px / (plotW || 1)) * (n - 1)))));
      }}
    >
      {width > 0 && (
        <svg width={width} height={height} className="block overflow-visible">
          {t.map((v) => (
            <g key={v}>
              <line x1={padL} x2={width - 8} y1={y(v)} y2={y(v)} stroke={v === 0 ? 'var(--color-line-strong)' : 'var(--color-line)'} strokeDasharray={v === 0 ? undefined : '2 3'} />
              <text x={padL - 8} y={y(v)} dy="0.32em" textAnchor="end" className="num fill-muted text-[10.5px]">
                {(axisFormat ?? format)(v)}
              </text>
            </g>
          ))}
          {area && series[0] && n > 1 && <path d={`${path(series[0].values)}L${x(n - 1)},${y(Math.max(lo, 0))}L${x(0)},${y(Math.max(lo, 0))}Z`} fill={series[0].color ?? PALETTE[0]} opacity={0.08} />}
          {series.map((s, si) => {
            const color = s.color ?? PALETTE[si];
            if (dashedFrom !== undefined && dashedFrom > 0 && dashedFrom < n) {
              return (
                <g key={s.name}>
                  <path d={path(s.values, 0, dashedFrom)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
                  <path d={path(s.values, dashedFrom)} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 4" strokeLinejoin="round" />
                </g>
              );
            }
            return <path key={s.name} d={path(s.values)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />;
          })}
          {labels.map((l, i) =>
            i % labelEvery(n, plotW) === 0 ? (
              <text key={l + i} x={x(i)} y={height - 6} textAnchor="middle" className="fill-muted text-[10.5px]">
                {l}
              </text>
            ) : null,
          )}
          {hover !== null && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + plotH} stroke="var(--color-line-strong)" />
              {series.map((s, si) => (
                <circle key={s.name} cx={x(hover)} cy={y(s.values[hover] ?? 0)} r={3.5} fill="var(--color-surface)" stroke={s.color ?? PALETTE[si]} strokeWidth={2} />
              ))}
            </g>
          )}
        </svg>
      )}
      {hover !== null && (
        <Readout x={x(hover)} width={width}>
          <div className="mb-1 font-medium text-ink">{labels[hover]}</div>
          {series.map((s, si) => (
            <div key={s.name} className="flex justify-between gap-2">
              <span className="flex items-center gap-1.5 text-muted">
                <span className="size-2 rounded-sm" style={{ background: s.color ?? PALETTE[si] }} />
                {s.name}
              </span>
              <span className="num text-ink">{format(s.values[hover] ?? 0)}</span>
            </div>
          ))}
        </Readout>
      )}
    </div>
  );
}

export function Sparkline({ values, color = 'var(--color-brand)', height = 28, className }: { values: number[]; color?: string; height?: number; className?: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  if (values.length < 2) return <div ref={ref} className={className} style={{ height }} />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const x = (i: number) => (width * i) / (values.length - 1);
  const y = (v: number) => 2 + (height - 4) * (1 - (v - min) / (max - min || 1));
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  return (
    <div ref={ref} className={cx('w-full', className)} style={{ height }} data-explain={sparkText(values)}>
      {width > 0 && (
        <svg width={width} height={height} className="block overflow-visible">
          <path d={`${d}L${width},${height}L0,${height}Z`} fill={color} opacity={0.08} />
          <path d={d} fill="none" stroke={color} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
          <circle cx={x(values.length - 1)} cy={y(values[values.length - 1]!)} r={2.2} fill={color} />
        </svg>
      )}
    </div>
  );
}

/** Horizontal share bars (top customers, spend by vendor). */
export function HBars({ rows, format, color = 'var(--color-brand)' }: { rows: { label: ReactNode; value: number; sub?: ReactNode }[]; format: (v: number) => string; color?: string }) {
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={i}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-[12.5px]">
            <span className="min-w-0 truncate text-ink">{r.label}</span>
            <span className="num shrink-0 text-ink-2">
              {format(r.value)}
              {r.sub && <span className="ml-1.5 text-muted">{r.sub}</span>}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-sunken">
            <div className="h-full rounded-full" style={{ width: `${(Math.abs(r.value) / max) * 100}%`, background: color }} />
          </div>
        </div>
      ))}
    </div>
  );
}
